import _ from 'lodash'
import { useMemo, useState } from 'react'
import { Cell, Pie, PieChart, Tooltip } from 'recharts'

import { Control } from '@/gen/inventory/v1/control_service_pb'

import { calculatePercentage } from '@/lib/collection/math-utils'
import { ControlImpact, IMPACT_TO_COLOR, controlImpactLabel } from '@/lib/risk/control-impact'
import { CHART_COLORS, type ChartTooltipColor, chartTooltipColorVariant } from '@/lib/ui/variants'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { PIE_CHART_CONFIG } from '@/pages/assessment/assessment-tabs/assessment-overview-tab/consts'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { Swatch } from '@/components/lemmala-design-system/swatch/swatch'
import { ControlStatusIcon } from '@/components/shared/icons/control-status'

import { groupControlsByImpact } from './control-weight-utils'

const MAX_CONTROLS_TO_SHOW = 6

type BasePieChartData = {
  value: number
  weight: number
  controls: Control[]
}

type UnvalidatedPieChartData = BasePieChartData & {
  type: 'unvalidated'
  impact: ControlImpact
}

type ValidatedPieChartData = BasePieChartData & {
  type: 'validated'
}

export type PieChartData = UnvalidatedPieChartData | ValidatedPieChartData

type RechartsPayloadEntry = {
  value: number
  dataKey: string
  name: string
  payload: PieChartData
  color: string
}

interface ControlWeightPieChartProps {
  controls: Control[]
}
type CustomTooltipContentProps = {
  active: boolean
  payload?: RechartsPayloadEntry[]
}

const CustomTooltipContent = ({ active, payload }: CustomTooltipContentProps) => {
  const { updateParam } = useUrlParams()

  if (!active || !payload || payload.length === 0) return null

  const entry = payload[0]
  const data = entry.payload

  if (data.type === 'validated') {
    return (
      <div className='rounded-lg border border-main-border-default bg-main-background-card px-3 py-2 shadow-lg'>
        <div className='mb-2 flex items-center gap-1 font-medium text-main-foreground-primary text-body-small'>
          <div
            className='size-3 flex-shrink-0 rounded'
            style={{ backgroundColor: CHART_COLORS['zinc'] }}
          />
          {data.controls.length} Validated controls
        </div>
        <div className='text-main-foreground-secondary text-body-small'>
          Weighted impact: {data.weight}
        </div>
      </div>
    )
  }

  return (
    <div className='max-w-72 rounded-lg border border-main-border-default bg-main-background-card p-2 shadow-lg'>
      <div className='mb-2 flex items-center gap-1 font-medium text-main-foreground-primary text-label-small'>
        <Swatch color={CHART_COLORS[IMPACT_TO_COLOR[data.impact]]} />
        <span className='truncate'>
          {data.controls.length} {controlImpactLabel[data.impact]} unvalidated
        </span>
      </div>
      <div className='text-main-foreground-secondary text-body-small'>
        Weighted impact: {data.weight}
      </div>
      <Separator className='my-2' />
      <div className='flex max-w-xs flex-col gap-1.5'>
        {data.controls.slice(0, MAX_CONTROLS_TO_SHOW).map((control, index) => (
          <div key={index} className='flex items-center gap-2'>
            <div>
              <ControlStatusIcon status={control.status} backgroundStyle='subtle' />
            </div>
            <span className='truncate text-main-foreground-primary text-body-small'>
              {control.title}
            </span>
          </div>
        ))}
        <Button
          variant='link'
          size='sm'
          className='self-start px-0'
          onClick={(e) => {
            e.stopPropagation()
            updateParam(UrlParam.ASSESSMENT_TAB, ASSESSMENT_TABS.ISSUES)
          }}
        >
          See all unvalidated controls
        </Button>
      </div>
    </div>
  )
}

export const ControlWeightPieChart = ({ controls }: ControlWeightPieChartProps) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const { chartData, unvalidatedCount, validatedCount, hasUnvalidated } = useMemo(() => {
    const grouped = groupControlsByImpact(controls)

    const totalWeight = _.sumBy(Object.values(grouped), (group) => group.weight)

    // total unvalidated controls count (not weight)
    const unvalidatedControlsCount = _.sumBy(
      Object.values(grouped).filter((group) => group.type === 'unvalidated'),
      (group) => group.controls.length,
    )

    const chartData: PieChartData[] = Object.values(ControlImpact)
      .filter((impact) => grouped[impact].weight > 0)
      .map((impact) => ({
        type: 'unvalidated' as const,
        value: calculatePercentage({ amount: grouped[impact].weight, total: totalWeight }),
        weight: grouped[impact].weight,
        controls: grouped[impact].controls,
        impact,
        color: IMPACT_TO_COLOR[impact],
      }))

    if (grouped.validated.weight > 0) {
      chartData.push({
        type: 'validated' as const,
        value: calculatePercentage({ amount: grouped.validated.weight, total: totalWeight }),
        weight: grouped.validated.weight,
        controls: grouped.validated.controls,
      })
    }

    return {
      chartData,
      unvalidatedCount: unvalidatedControlsCount,
      validatedCount: grouped.validated.controls.length,
      hasUnvalidated: unvalidatedControlsCount > 0,
    }
  }, [controls])

  return (
    <PieChart
      onMouseLeave={() => setIsTooltipVisible(false)}
      accessibilityLayer={false}
      width={160}
      height={160}
    >
      <Pie
        onMouseEnter={() => setIsTooltipVisible(true)}
        className='outline-none'
        data={chartData}
        isAnimationActive={false}
        {...{ ...PIE_CHART_CONFIG, innerRadius: 45, outerRadius: 72 }}
      >
        {chartData.map((entry, index) => {
          const color: ChartTooltipColor =
            entry.type === 'unvalidated' ? IMPACT_TO_COLOR[entry.impact] : 'zinc'

          return <Cell key={`cell-${index}`} fill={chartTooltipColorVariant({ color: color })} />
        })}
      </Pie>
      <Tooltip
        active={isTooltipVisible}
        isAnimationActive={false}
        cursor={false}
        wrapperStyle={{ pointerEvents: 'auto' }}
        allowEscapeViewBox={{ x: true, y: true }}
        content={({ active, payload }) => (
          <CustomTooltipContent active={active} payload={payload as RechartsPayloadEntry[]} />
        )}
      />
      <text
        x={'50%'}
        y={'42%'}
        textAnchor='middle'
        dominantBaseline='middle'
        className={
          hasUnvalidated
            ? 'fill-main-foreground-danger text-heading-large'
            : 'fill-main-foreground-success text-heading-large'
        }
      >
        {hasUnvalidated ? unvalidatedCount : validatedCount}
      </text>
      <text
        x={'50%'}
        y={'56%'}
        textAnchor='middle'
        dominantBaseline='middle'
        className={
          hasUnvalidated
            ? 'fill-main-foreground-danger text-body-small'
            : 'fill-main-foreground-success text-body-small'
        }
      >
        {hasUnvalidated ? 'Unvalidated' : 'Validated'}
      </text>
    </PieChart>
  )
}
